<template>
  <div>

    <!-- Discard Article Button & Modal -->
    <div v-if="!isArticleReadOnly" class="block">
      <button
        id="btn-discard-article"
        type="button"
        class="button is-danger"
        @click="toggleDiscardArticlePrompt"
        :disabled="isFormLocked"
      >
        <span class="icon"><i class="fas fa-trash-alt"></i></span>
        <span>Discard article</span>
      </button>

      <ModalWindow
        :isOpen="isDiscardArticlePromptVisible"
        :heading="`Discard ${article.marinLitId ? article.marinLitId : 'new article'}`"
        :disabled="isFormSaving"
        @close="toggleDiscardArticlePrompt"
      >
        <template #body>
          <p>Do you really want to discard this article?</p>
          <p>This action is permanent and irreversible.</p>
        </template>
        <template #footer>
          <button
            type="button"
            class="button is-danger"
            :class="{ 'is-loading': isFormSaving }"
            @click="discardArticle"
          >Discard</button>
        </template>
      </ModalWindow>
    </div>

    <!-- Save Error -->
    <MessageBlock v-if="saveError" heading="Failed to save article" class="is-danger">
      <p>{{ saveError }}</p>
    </MessageBlock>

    <!-- Form -->
    <VForm @submit="saveArticle">
      <fieldset :disabled="isFormLocked">

        <!-- ROW 1: Article Title -->
        <div class="columns is-mobile">
          <div class="column is-2">
            <label class="label">Article title<RequiredAsterisk /></label>
          </div>
          <div class="column is-10">
            <Field 
              name="title" 
              rules="html_required:title" 
              :modelValue="title"
              v-slot="{ field, errors }"
            >
              <div class="control" :class="{ 'is-danger': !!errors.length }">
                <HtmlEditor 
                  id="title" 
                  :disabled="isFormLocked"
                  :modelValue="field.value"
                  @update:modelValue="value => { field.onChange(value); title = value }"
                  :init="TinyMceConfig" 
                />
                <p v-if="!!errors.length" class="help is-danger">{{ errors[0] }}</p>
              </div>
            </Field>
          </div>
        </div>

        <!-- ROW 2: Authors -->
        <div class="columns is-mobile mb-1" v-for="(author, index) in authors" :key="index">
          <div v-if="index === 0" class="column is-2">
            <label class="label">Author(s)</label>
          </div>
          <div class="column is-4" :class="{ 'is-offset-2': index > 0 }">
            <TextInput :id="`firstName${index}`" v-model="author.firstName" placeholder="Given name" />
          </div>
          <div class="column is-4">
            <TextInput :id="`lastName${index}`" v-model="author.lastName" placeholder="Family name" />
          </div>
          <div class="column is-2">
            <button v-if="index === 0" type="button" id="btn-add-author" class="button is-link is-outlined is-fullwidth" @click="addAuthorRowAction">
              <span class="icon"><i class="fas fa-plus"></i></span>
              <span>Add author</span>
            </button>
            <button v-else type="button" :id="`btn-remove-author-${index}`" class="button is-outlined is-fullwidth" @click="deleteAuthorRow(index)">
              <span class="icon"><i class="fas fa-trash-alt"></i></span>
              <span>Remove</span>
            </button>
          </div>
        </div>

        <!-- ROW 3: Journal & DOI -->
        <div class="columns is-mobile mt-1">
          <div class="column is-2">
            <label for="journalTitle" class="label">Journal</label>
          </div>
          <div class="column is-4">
            <TextInput id="journalTitle" v-model="journalTitle" placeholder="Journal" />
          </div>
          <div class="column is-narrow is-offset-1">
            <label for="doi" class="label">DOI</label>
          </div>
          <div class="column">
            <TextInput id="doi" rules="doi" v-model.trim="doi" placeholder="DOI" />
          </div>
        </div>

        <!-- ROW 4: Volume, Issue, Year, Page Range -->
        <div class="columns is-mobile">
          <div class="column is-2">
            <label for="journalVolume" class="label">Volume</label>
          </div>
          <div class="column is-1">
            <TextInput id="journalVolume" v-model.trim="journalVolume" placeholder="Volume" />
          </div>
          <div class="column is-1 is-offset-1 has-text-right">
            <label for="issueNumber" class="label">Issue</label>
          </div>
          <div class="column is-1">
            <TextInput id="issueNumber" v-model.trim="issueNumber" placeholder="Issue" />
          </div>
          <div class="column is-1 has-text-right">
            <label for="subsYear" class="label">Year</label>
          </div>
          <div class="column is-1">
            <TextInput id="subsYear" v-model.trim="subsYear" :maxlength="4" placeholder="Year" />
          </div>
          <div class="column is-2 has-text-right">
            <label class="label">Page range</label>
          </div>
          <div class="column is-1">
            <TextInput id="firstPage" v-model.trim="firstPage" placeholder="First" />
          </div>
          <div class="column is-1">
            <TextInput id="lastPage" v-model.trim="lastPage" placeholder="Last" />
          </div>
        </div>
        <NextPreviousButtons v-if="!isArticleReadOnly" next="Classification" :showThrobber="isFormSaving">
          <template #prev>
            <button type="button" @click="$router.push({ name: 'MarinLit' })" class="button">
              <span class="icon"><i class="fas fa-backward"></i></span>
              <span>Back to article list</span>
            </button>
          </template>
        </NextPreviousButtons>
      </fieldset>
    </VForm>
  </div>
</template>

<script>
import { Form as VForm, Field } from 'vee-validate';
import ArticleTabsMixin from '@/mixins/marinlit/article-tabs-mixin';
import RequiredAsterisk from "@/components/forms/required-asterisk.vue";
import { useMarinLitEditorStore } from '@/store/modules/marinlit/editor';

export default {
  name: 'TabBasic',
  components: { VForm, Field, RequiredAsterisk },
  mixins: [ArticleTabsMixin],
  setup() {
    const marinLitEditorStore = useMarinLitEditorStore();

    return {
      marinLitEditorStore,
    };
  },
  data() {
    return {
      isDiscardArticlePromptVisible: false,
    };
  },
  computed: {
    title: {
      get() {
        return this.marinLitEditorStore.article.title;
      },
      set(value) {
        this.marinLitEditorStore.updateField({
          path: 'article.title',
          value,
        });
      },
    },

    journalTitle: {
      get() {
        return this.marinLitEditorStore.article.journalTitle;
      },
      set(value) {
        this.marinLitEditorStore.updateField({
          path: 'article.journalTitle',
          value,
        });
      },
    },

    doi: {
      get() {
        return this.marinLitEditorStore.article.doi;
      },
      set(value) {
        this.marinLitEditorStore.updateField({
          path: 'article.doi',
          value,
        });
      },
    },

    journalVolume: {
      get() {
        return this.marinLitEditorStore.article.journalVolume;
      },
      set(value) {
        this.marinLitEditorStore.updateField({
          path: 'article.journalVolume',
          value,
        });
      },
    },

    issueNumber: {
      get() {
        return this.marinLitEditorStore.article.issueNumber;
      },
      set(value) {
        this.marinLitEditorStore.updateField({
          path: 'article.issueNumber',
          value,
        });
      },
    },

    subsYear: {
      get() {
        return this.marinLitEditorStore.article.subsYear;
      },
      set(value) {
        this.marinLitEditorStore.updateField({
          path: 'article.subsYear',
          value,
        });
      },
    },

    firstPage: {
      get() {
        return this.marinLitEditorStore.article.firstPage;
      },
      set(value) {
        this.marinLitEditorStore.updateField({
          path: 'article.firstPage',
          value,
        });
      },
    },

    lastPage: {
      get() {
        return this.marinLitEditorStore.article.lastPage;
      },
      set(value) {
        this.marinLitEditorStore.updateField({
          path: 'article.lastPage',
          value,
        });
      },
    },

    // Multi-row (authors)
    authors: {
      get() {
        return this.marinLitEditorStore.article.authors;
      },
      set(value) {
        this.marinLitEditorStore.updateField({
          path: 'article.authors',
          value,
        });
      },
    },
  },
  methods: {
    addAuthorRowAction() {
      this.marinLitEditorStore.addAuthorRowAction();
    },
    deleteMultiRowValueAction({ key, index }) {
      this.marinLitEditorStore.deleteMultiRowValueAction({ key, index });
    },
    // Override the mixin because VeeValidate 4's handleSubmit function
    // discards the native form submission, including e.submitter.value,
    // so we have to provide 'Classification' (next tab) manually.
    async saveArticle() {
      this.$emit('saveArticle', 'Classification');
    },
    async discardArticle() {
      this.$emit('discardArticle');
    },
    toggleDiscardArticlePrompt() {
      this.isDiscardArticlePromptVisible = !this.isDiscardArticlePromptVisible;
    },
    deleteAuthorRow(index) {
      this.deleteMultiRowValueAction({ key: 'authors', index });
    },
  },
};
</script>